import {NEW_BANK_ACCOUNT_ID} from '../CreateBuilder/BuilderSourceDestination/builderConstants';
import {
  nonEmptyStringOrNull,
  getPotContactId,
  updateRecsVarsProps,
  upsertBuilderHeader,
} from './builderHelpers';
import {
  OVRecommendActionEnum,
  RecurringFrequencyEnum,
  BankDetailsEnum,
} from '../../../schema-types/globalTypes';
import validateUUID from '../../../utils/validateUUID';
import {toUUIDOrNull} from '../../../graphql/uuid';
import {getISODateOrUndefined} from '../../../utils/getISODate';
import OWNERSHIP from '../../../constants/Ownership';
import FREQUENCY from '../../../constants/Frequency';

// FREQUENCY (Title Case, e.g. 'Monthly') -> RecurringFrequencyEnum (ALL CAPS) the
// withdrawal mutation expects. The two enums share keys but differ in value.
const FREQUENCY_TO_RECURRING: Record<FREQUENCY, RecurringFrequencyEnum> = {
  [FREQUENCY.SINGLE]: RecurringFrequencyEnum.SINGLE,
  [FREQUENCY.MONTHLY]: RecurringFrequencyEnum.MONTHLY,
  [FREQUENCY.QUARTERLY]: RecurringFrequencyEnum.QUARTERLY,
  [FREQUENCY.ANNUAL]: RecurringFrequencyEnum.ANNUAL,
};

export const saveWithdrawal = async (props: updateRecsVarsProps): Promise<string> => {
  const {accountId, values, builderDetails} = props;
  const {withdraw, contact, urgency, urgencyReason} = values;

  if (!withdraw?.destination) {
    throw Error('Must have a destination pot');
  }

  const isNewPlanRequired = withdraw.destination.id === NEW_BANK_ACCOUNT_ID;

  const ovrecommendId = await upsertBuilderHeader(props, {
    action: OVRecommendActionEnum.WITHDRAW,
    accountId: validateUUID(accountId),
    goalId: validateUUID(values.goal?.id),
    isNewPlanRequired,
    contactId: getPotContactId(contact),
    withdrawalReason: nonEmptyStringOrNull(
      withdraw.source?.reason || builderDetails?.withdrawalReason
    ),
    urgency,
    urgencyReason,
    startDate:
      getISODateOrUndefined(withdraw.source?.start) ||
      getISODateOrUndefined(builderDetails?.startDate),
    endDate:
      getISODateOrUndefined(withdraw.source?.end) ||
      getISODateOrUndefined(builderDetails?.endDate),
  });

  const dest = withdraw.destination;
  const isBankDetailsKnown = dest.bankDetails !== BankDetailsEnum.NOT_KNOWN;

  const withdrawalFrequency =
    FREQUENCY_TO_RECURRING[withdraw.source.frequency] ?? RecurringFrequencyEnum.SINGLE;
  const isRecurring = withdrawalFrequency !== RecurringFrequencyEnum.SINGLE;

  const withdrawResult = await props.upsertBuilderWithdraw({
    variables: {
      ovrecommendId: validateUUID(ovrecommendId)!,
      isFull: Boolean(withdraw.source.full),
      isWithdrawToNewBank: isNewPlanRequired,
      sourcePotId: withdraw.source.id,
      destinationPotId: isNewPlanRequired ? null : validateUUID(dest.id),
      withdrawalFrequency,
      withdrawalAmount:
        Number(
          isRecurring
            ? withdraw.source.recurringClientAmount
            : withdraw.source.singleClientAmount
        ) || 0,
      // New-bank-account fields — only sent when creating a new bank
      productProviderId: isNewPlanRequired ? toUUIDOrNull(dest.productProvider) : null,
      contactId: isNewPlanRequired
        ? dest.contactId === OWNERSHIP.JOINT_CODE
          ? null
          : toUUIDOrNull(dest.contactId)
        : null,
      planNumber: isNewPlanRequired && isBankDetailsKnown ? dest.accountNumber : null,
      sortCode:
        isNewPlanRequired && isBankDetailsKnown
          ? dest.sortCode?.replaceAll('-', '')
          : null,
      policyHolder: isNewPlanRequired ? dest.accountName : null,
      bankDetails: isNewPlanRequired ? dest.bankDetails : null,
    },
  });

  if (!withdrawResult.data?.upsertWithdraw?.ok) {
    const errors = withdrawResult.data?.upsertWithdraw?.errors?.join('. ');
    throw Error(errors || 'Failed to save withdrawal');
  }

  return ovrecommendId;
};
